import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Upload, FileText, Loader2, CheckCircle2, AlertTriangle,
  ChevronDown, ChevronRight, Eye, Search, Shield, BookOpen,
  BarChart3, Zap, X, Download, FileUp
} from "lucide-react";

const ReportLoaderPage = () => {
  const { t } = useTranslation('operations');
  const [loadedReports, setLoadedReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(["narrative", "standards", "defense", "gaps"]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/auditiq/loaded-reports?limit=20`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setLoadedReports(d.reports || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/auditiq/load-report`, { method: "POST", credentials: "include", body: fd });
      if (res.ok) {
        const data = await res.json();
        setSelectedReport(data);
        fetchReports();
        // Poll for completion if processing
        if (data.status === "processing") {
          pollForCompletion(data.report_id);
        }
      } else {
        const err = await res.json();
        alert(err.detail || "Upload failed");
      }
    } catch (e) { console.error(e); alert("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const pollForCompletion = async (reportId) => {
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await fetch(`${API}/auditiq/loaded-reports/${reportId}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setSelectedReport(data);
          if (data.status === "complete" || data.status === "error") return;
        }
      } catch (e) { console.error(e); }
    }
  };

  const viewReport = async (reportId) => {
    try {
      const res = await fetch(`${API}/auditiq/loaded-reports/${reportId}`, { credentials: "include" });
      if (res.ok) { setSelectedReport(await res.json()); }
    } catch (e) { console.error(e); }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
  };

  const SectionHeader = ({ id, title, icon: Icon, color, count }) => (
    <button onClick={() => toggleSection(id)} className="w-full flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className={`text-xs font-bold uppercase tracking-wider ${color}`}>{title}</span>
        {count !== undefined && <span className="text-[10px] text-[#94A3B8] bg-[#132340] px-1.5 py-0.5 rounded">{count}</span>}
      </div>
      {expandedSections.includes(id) ? <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />}
    </button>
  );

  const ext = selectedReport?.extracted_data;
  const intel = selectedReport?.intelligence;

  return (
    <QMSLayout title={t('operations:reports.title')}>
      <div data-testid="report-loader-page" className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1A6BCC]/20 flex items-center justify-center">
              <FileUp className="w-5 h-5 text-[#1A6BCC]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>Live Report Loader</h1>
              <p className="text-xs text-[#94A3B8]">Retroactive Intelligence Engine — Upload any report, get instant defense</p>
            </div>
          </div>
          <label data-testid="upload-report-btn" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold cursor-pointer transition-colors ${uploading ? "bg-[#132340] text-[#94A3B8]" : "bg-[#1A6BCC] text-white hover:bg-[#00C8E0]"}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? t('operations:reportLoader.analyzing') : t('operations:reportLoader.uploadReport')}
            <input type="file" accept=".pdf,.docx,.xlsx,.csv,.txt" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: "calc(100vh - 200px)" }}>
          {/* Left: Report list + extracted data */}
          <div className="lg:col-span-5 space-y-3">
            {/* Report list */}
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-bold text-[#00C8E0] uppercase tracking-wider">Loaded Reports</h3>
              {loading ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-[#94A3B8] animate-spin" /></div>
              ) : loadedReports.length === 0 ? (
                <div className="text-center py-6">
                  <FileText className="w-10 h-10 text-[#1E3A5F] mx-auto mb-2" />
                  <p className="text-[#94A3B8] text-sm">No reports loaded yet.</p>
                  <p className="text-[#94A3B8]/60 text-xs">Upload a PDF, DOCX, XLSX, or CSV to begin.</p>
                </div>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {loadedReports.map(r => (
                    <button key={r.report_id} onClick={() => viewReport(r.report_id)} data-testid="loaded-report-item"
                      className={`w-full text-left p-2 rounded-lg transition-colors text-xs flex items-center justify-between ${selectedReport?.report_id === r.report_id ? "bg-[#1A6BCC]/20 border border-[#1A6BCC]/30" : "hover:bg-[#132340]"}`}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-[#1A6BCC]" />
                        <div>
                          <span className="text-white block">{r.filename}</span>
                          <span className="text-[#94A3B8] text-[10px]">{r.report_id}</span>
                        </div>
                      </div>
                      <span className="text-[#94A3B8] text-[10px]">{r.format?.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Extracted values */}
            {selectedReport && ext && (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-[#00C8E0] uppercase tracking-wider">Extracted Data</h3>
                {ext.summary && <p className="text-sm text-[#94A3B8]">{ext.summary}</p>}

                {/* Extracted values table */}
                {ext.extracted_values?.length > 0 && (
                  <div>
                    <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Statistical Values</span>
                    <div className="mt-1 space-y-0.5">
                      {ext.extracted_values.map((v, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-[#1E3A5F]/50 last:border-0">
                          <span className="text-[#94A3B8]">{v.metric}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-mono">{v.value}{v.unit ? ` ${v.unit}` : ""}</span>
                            {v.status && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${v.status === "PASS" ? "bg-emerald-500/20 text-emerald-400" : v.status === "FAIL" ? "bg-red-500/20 text-red-400" : "bg-[#132340] text-[#94A3B8]"}`}>{v.status}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Test results */}
                {ext.test_results?.length > 0 && (
                  <div>
                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Test Results</span>
                    <div className="mt-1 space-y-0.5">
                      {ext.test_results.map((t, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-[#1E3A5F]/50 last:border-0">
                          <span className="text-[#94A3B8]">{t.parameter}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-mono text-[10px]">{t.value || ""}{t.spec ? ` (${t.spec})` : ""}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${t.result === "PASS" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>{t.result}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Standards referenced */}
                {ext.standards_referenced?.length > 0 && (
                  <div>
                    <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Standards Referenced</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {ext.standards_referenced.map((s, i) => (
                        <span key={i} className="px-2 py-0.5 rounded bg-[#132340] text-[10px] text-[#94A3B8]">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Excursions */}
                {ext.excursions?.length > 0 && (
                  <div>
                    <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Excursions Detected</span>
                    <div className="mt-1 space-y-1">
                      {ext.excursions.map((e, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs p-2 bg-red-500/10 rounded border border-red-500/20">
                          <AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-red-400 font-medium">{e.severity}</span>
                            <span className="text-[#94A3B8] ml-1">{e.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Generated Intelligence */}
          <div className="lg:col-span-7 space-y-3">
            {!selectedReport ? (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-12 text-center">
                <FileUp className="w-16 h-16 text-[#1E3A5F] mx-auto mb-4" />
                <h3 className="text-lg text-[#94A3B8] font-medium mb-1">Upload a Report to Begin</h3>
                <p className="text-sm text-[#94A3B8]/60">Accepts PDF, DOCX, XLSX, CSV. CHARSTAN will extract all data and generate audit-ready intelligence.</p>
              </div>
            ) : (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#00C8E0] uppercase tracking-wider">Generated Intelligence</h3>
                  <div className="flex items-center gap-2">
                    {selectedReport.status === "processing" && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-bold">
                        <Loader2 className="w-3 h-3 animate-spin" /> Processing...
                      </span>
                    )}
                    {selectedReport.status === "complete" && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">
                        <CheckCircle2 className="w-3 h-3" /> Complete
                      </span>
                    )}
                    {selectedReport.status === "error" && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/20 text-red-400 text-[10px] font-bold">
                        <AlertTriangle className="w-3 h-3" /> Error
                      </span>
                    )}
                    <span className="text-[10px] text-[#94A3B8] font-mono">{selectedReport.report_id}</span>
                  </div>
                </div>

                {selectedReport.status === "processing" && (
                  <div className="text-center py-12">
                    <Loader2 className="w-10 h-10 text-[#00C8E0] animate-spin mx-auto mb-3" />
                    <p className="text-[#94A3B8] text-sm">AI is analyzing your report...</p>
                    <p className="text-[#94A3B8]/60 text-xs mt-1">Extracting values, mapping standards, generating defense scripts.</p>
                  </div>
                )}

                {intel ? (
                  <>
                    {/* Validation Narrative */}
                    <div className="border-b border-[#1E3A5F] pb-3">
                      <SectionHeader id="narrative" title={t('operations:reportLoader.validationNarrative')} icon={FileText} color="text-[#00C8E0]" />
                      {expandedSections.includes("narrative") && intel.validation_narrative && (
                        <p className="text-sm text-[#94A3B8] leading-relaxed whitespace-pre-line">{intel.validation_narrative}</p>
                      )}
                    </div>

                    {/* Statistical Models */}
                    {intel.statistical_models?.length > 0 && (
                      <div className="border-b border-[#1E3A5F] pb-3">
                        <SectionHeader id="models" title={t('operations:reportLoader.statisticalModels')} icon={BarChart3} color="text-purple-400" count={intel.statistical_models.length} />
                        {expandedSections.includes("models") && (
                          <div className="space-y-2">
                            {intel.statistical_models.map((m, i) => (
                              <div key={i} className="p-2 rounded-lg bg-[#132340]">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-white font-medium">{m.metric}</span>
                                  {m.value && <span className="text-xs text-[#00C8E0] font-mono">{m.value}</span>}
                                </div>
                                {m.formula && <p className="text-[10px] text-[#94A3B8] font-mono mb-1">{m.formula}</p>}
                                {m.explanation && <p className="text-xs text-[#94A3B8]">{m.explanation}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Standards Mapping */}
                    {intel.standards_mapping?.length > 0 && (
                      <div className="border-b border-[#1E3A5F] pb-3">
                        <SectionHeader id="standards" title={t('operations:reportLoader.standardsMapping')} icon={BookOpen} color="text-amber-400" count={intel.standards_mapping.length} />
                        {expandedSections.includes("standards") && (
                          <div className="space-y-1">
                            {intel.standards_mapping.map((s, i) => (
                              <div key={i} className="flex items-center justify-between p-2 rounded bg-[#132340] text-xs">
                                <div>
                                  <span className="text-white">{s.standard}</span>
                                  {s.clause && <span className="text-[#94A3B8] ml-1">{s.clause}</span>}
                                  {s.applicability && <span className="text-[#94A3B8]/70 block text-[10px] mt-0.5">{s.applicability}</span>}
                                </div>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${s.status === "COMPLIANT" ? "bg-emerald-500/20 text-emerald-400" : s.status === "GAP" ? "bg-red-500/20 text-red-400" : "bg-[#132340] text-[#94A3B8] border border-[#1E3A5F]"}`}>{s.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Audit Defense */}
                    {intel.audit_defense?.length > 0 && (
                      <div className="border-b border-[#1E3A5F] pb-3">
                        <SectionHeader id="defense" title={t('operations:reportLoader.auditDefenseScripts')} icon={Shield} color="text-emerald-400" count={intel.audit_defense.length} />
                        {expandedSections.includes("defense") && (
                          <div className="space-y-2">
                            {intel.audit_defense.map((d, i) => (
                              <div key={i} className="p-3 rounded-lg bg-[#132340] border border-[#1E3A5F]">
                                <div className="flex items-start gap-2 mb-2">
                                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs text-amber-400 font-medium">{d.question}</span>
                                </div>
                                <div className="ml-5 space-y-1">
                                  <p className="text-xs text-white leading-relaxed">{d.response}</p>
                                  {d.evidence && <p className="text-[10px] text-[#94A3B8]">Evidence needed: {d.evidence}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Excursion Analysis */}
                    {intel.excursion_analysis?.length > 0 && (
                      <div className="border-b border-[#1E3A5F] pb-3">
                        <SectionHeader id="excursions" title={t('operations:reportLoader.excursionAnalysis')} icon={AlertTriangle} color="text-red-400" count={intel.excursion_analysis.length} />
                        {expandedSections.includes("excursions") && (
                          <div className="space-y-1">
                            {intel.excursion_analysis.map((e, i) => (
                              <div key={i} className="p-2 rounded bg-red-500/10 border border-red-500/20 text-xs">
                                <span className="text-red-400 font-medium">{e.issue}</span>
                                <p className="text-[#94A3B8] mt-1">{e.rationale}</p>
                                {e.recommendation && <p className="text-[#94A3B8]/70 text-[10px] mt-1">Recommendation: {e.recommendation}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Gap Identification */}
                    {intel.gap_identification?.length > 0 && (
                      <div>
                        <SectionHeader id="gaps" title={t('operations:reportLoader.gapIdentification')} icon={Search} color="text-orange-400" count={intel.gap_identification.length} />
                        {expandedSections.includes("gaps") && (
                          <div className="space-y-1">
                            {intel.gap_identification.map((g, i) => (
                              <div key={i} className="p-2 rounded bg-orange-500/10 border border-orange-500/20 text-xs">
                                <span className="text-orange-400 font-medium">{g.gap}</span>
                                {g.impact && <span className="text-[#94A3B8] ml-2">Risk: {g.impact}</span>}
                                {g.recommendation && <p className="text-[#94A3B8]/70 text-[10px] mt-1">{g.recommendation}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-[#94A3B8] animate-spin mx-auto mb-2" />
                    <p className="text-[#94A3B8] text-sm">Generating intelligence...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default ReportLoaderPage;
